import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LeadHeader from '../../../components/LeadHeader/LeadHeader'
import PageLoading from '../../../components/PageLoading/PageLoading'
import useLead from '../../../hooks/useLead'
import DatosContacto from './components/DatosContacto/DatosContacto'
import DatosTelefono from './components/DatosTelefono/DatosTelefono'
import DatosEmail from './components/DatosEmail/DatosEmail'
import DatosEstudios from './components/DatosEstudios/DatosEstudios'
import DatosMarketing from './components/DatosMarketing/DatosMarketing'
import ModalEditarDatosContactoLead from '../../../modals/ModalEditarDatosContactoLead/ModalEditarDatosContactoLead'
import ModalEditarDatosTelefono from '../../../modals/ModalEditarDatosTelefono/ModalEditarDatosTelefono'
import ModalEditarDatosEmail from '../../../modals/ModalEditarDatosEmail/ModalEditarDatosEmail'
import ModalEditarDatosMarketingLead from '../../../modals/ModalEditarDatosMarketingLead/ModalEditarDatosMarketingLead'
import ModalEditarDatosEstudiosLead from '../../../modals/ModalEditarDatosEstudiosLead/ModalEditarDatosEstudiosLead'
import {
  updateLead,
  updateTelefonos,
  updateEmails
} from '../../../utils/api/leads'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import css from './Ficha.module.css'

const Ficha = () => {
  const notification = useNotification()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { isLoading, lead } = useLead()

  const [isEditarDatosContactoOpen, setEditarDatosContactoOpen] =
    useState(false)
  const [isEditarDatosTelefonoOpen, setEditarDatosTelefonoOpen] =
    useState(false)
  const [isEditarDatosEmailOpen, setEditarDatosEmailOpen] = useState(false)
  const [isEditarDatosEstudiosOpen, setEditarDatosEstudiosOpen] =
    useState(false)
  const [isEditarDatosMarketingOpen, setEditarDatosMarketingOpen] =
    useState(false)

  const { isPending: isLeadUpdating, mutate: actualizarLead } = useMutation({
    mutationFn: updateLead,
    onSuccess: () => {
      notification.success({
        title: 'Academia cambiado',
        content: 'Se ha actualizado el lead'
      })
      setEditarDatosContactoOpen(false)
      setEditarDatosEstudiosOpen(false)
      setEditarDatosMarketingOpen(false)
      queryClient.invalidateQueries({
        queryKey: ['marketing', 'lead', lead.id]
      })
      queryClient.invalidateQueries({
        queryKey: ['marketing', 'feedback', 'list', 'CRM']
      })
    },
    onError: err =>
      notification.error({ title: 'Error al modificar el lead', content: err })
  })

  const { isPending: isTelefonosUpdating, mutate: actualizarTelefonos } =
    useMutation({
      mutationFn: updateTelefonos,
      onSuccess: () => {
        notification.success({
          title: 'Teléfonos actualizados',
          content: 'Se han actualizado los teléfonos del lead'
        })
        setEditarDatosTelefonoOpen(false)
        queryClient.invalidateQueries({
          queryKey: ['marketing', 'lead', lead.id]
        })
        queryClient.invalidateQueries({
          queryKey: ['marketing', 'feedback', 'list', 'CRM']
        })
      },
      onError: err =>
        notification.error({
          title: 'Error al actualizar teléfonos',
          content: err
        })
    })

  const { isPending: isEmailsUpdating, mutate: actualizarEmails } = useMutation(
    {
      mutationFn: updateEmails,
      onSuccess: () => {
        notification.success({
          title: 'Emails actualizados',
          content: 'Se han actualizado los emails del lead'
        })
        setEditarDatosEmailOpen(false)
        queryClient.invalidateQueries({
          queryKey: ['marketing', 'lead', lead.id]
        })
        queryClient.invalidateQueries({
          queryKey: ['marketing', 'feedback', 'list', 'CRM']
        })
      },
      onError: err =>
        notification.error({
          title: 'Error al actualizar emails',
          content: err
        })
    }
  )

  useEffect(() => {
    if (!lead) navigate('/crm')
  }, [lead, navigate])

  const handleUpdateLead = datos => {
    if (isLeadUpdating) return
    actualizarLead(datos)
  }

  const handleUpdateTelefonos = telefonos => {
    if (isTelefonosUpdating) return
    actualizarTelefonos({ id: lead.id, telefonos })
  }

  const handleUpdateEmails = emails => {
    if (isEmailsUpdating) return
    actualizarEmails({ id: lead.id, emails })
  }

  return (
    <PageLoading isLoading={isLoading}>
      <div className={css.main}>
        <LeadHeader label='Ficha' />
        <div className={css.content}>
          <div className={css.accordion}>
            <DatosContacto
              lead={lead}
              onEdit={() => setEditarDatosContactoOpen(true)}
            />
          </div>
          <div className={css.accordion}>
            <DatosTelefono
              lead={lead}
              onEdit={() => setEditarDatosTelefonoOpen(true)}
            />
          </div>
          <div className={css.accordion}>
            <DatosEmail
              lead={lead}
              onEdit={() => setEditarDatosEmailOpen(true)}
            />
          </div>
          <div className={css.accordion}>
            <DatosEstudios
              lead={lead}
              onEdit={() => setEditarDatosEstudiosOpen(true)}
            />
          </div>
          <div className={css.accordion}>
            <DatosMarketing
              lead={lead}
              onEdit={() => setEditarDatosMarketingOpen(true)}
            />
          </div>
        </div>
      </div>
      <ModalEditarDatosContactoLead
        open={isEditarDatosContactoOpen}
        lead={lead}
        onClose={() => setEditarDatosContactoOpen(false)}
        onSubmit={handleUpdateLead}
      />
      <ModalEditarDatosTelefono
        open={isEditarDatosTelefonoOpen}
        telefonosContacto={lead.telefonos}
        onClose={() => setEditarDatosTelefonoOpen(false)}
        onSubmit={handleUpdateTelefonos}
      />
      <ModalEditarDatosEmail
        open={isEditarDatosEmailOpen}
        emailsContacto={lead.emails}
        onClose={() => setEditarDatosEmailOpen(false)}
        onSubmit={handleUpdateEmails}
      />
      <ModalEditarDatosMarketingLead
        open={isEditarDatosMarketingOpen}
        lead={lead}
        onClose={() => setEditarDatosMarketingOpen(false)}
        onSubmit={handleUpdateLead}
      />
      <ModalEditarDatosEstudiosLead
        open={isEditarDatosEstudiosOpen}
        lead={lead}
        onClose={() => setEditarDatosEstudiosOpen(false)}
        onSubmit={handleUpdateLead}
      />
    </PageLoading>
  )
}

export default Ficha
